import { answerRefJointReach, answerRefReach, answerRefResolves, } from './answer-ref.js';
/**
 * Whether one member is usable from `referringTaskId` — the same two questions as a bare
 * reference, asked once per member.
 */
function memberReach(ref, referringTaskId, tasks, site) {
    const source = tasks.find(t => t.id === ref.taskId);
    const block = source?.blocks?.find(b => b.id === ref.blockId);
    if (!answerRefResolves(ref, block?.type, block?.data ?? undefined))
        return 'never';
    return answerRefReach(ref.taskId, referringTaskId, tasks, site);
}
/**
 * An alias's health where it is USED, which is not a property of the alias alone.
 *
 * The rule reads off the members as a set, because that is how a teacher means them:
 *
 *   `ok`          — some member is ALWAYS reached. Whatever route the student took, one of
 *                   these answers exists, so the sentence is never empty.
 *   `conditional` — members are reachable, but only sometimes, and nothing guarantees one.
 *                   Legitimate as often as not, which is why it carries no warning — but it IS
 *                   the case a fallback exists for.
 *   `broken`      — no member can ever have been answered before here. Nothing to show and
 *                   nothing a fallback should paper over.
 *
 * JOINT COVERAGE IS COMPUTED, which it once was not. The note here used to say two members could
 * cover everything while neither covered it alone, and that proving it needed path analysis the
 * graph over-approximates. The first half is true and common — three pathways, each ending in
 * its own copy of the question, the three answers joined under one alias — and the second half
 * was wrong about the cost: `always` was ALREADY dominance by deleting the source and retrying
 * the traversal, so the set case is the same traversal with a set. See `answerRefJointReach`.
 *
 * The over-approximation stands and is still the safe direction: an edge exists wherever a
 * condition could route along it, which makes domination harder to prove, never easier. So an
 * `ok` here is trustworthy, and a `conditional` may still be a route nobody takes.
 */
export function aliasStatus(alias, referringTaskId, tasks, 
/** Where the chip SITS — see `ReachSite`. Only same-task members read differently. */
site) {
    const reaches = alias.members.map(m => memberReach(m, referringTaskId, tasks, site));
    // One member that always lands settles it without a second traversal, and settles it the same
    // way it always did.
    if (reaches.some(r => r === 'always'))
        return 'ok';
    if (!reaches.some(r => r === 'sometimes'))
        return 'broken';
    /*
     * Several members, none of them certain alone. Ask whether they are certain TOGETHER, counting
     * only the ones that could actually carry an answer here: a member whose block is gone or whose
     * facet no longer resolves is `never`, and a path through it still arrives empty.
     */
    const live = alias.members.filter((_, i) => reaches[i] === 'sometimes').map(m => m.taskId);
    /* Joint reach needs no site: a member that is same-task has already been settled above —
       `never` in a block, `always` at an exit — so it is never one of the `sometimes` set. */
    return answerRefJointReach(live, referringTaskId, tasks) === 'always' ? 'ok' : 'conditional';
}
/**
 * Which member to read, given where the chip sits and what the student has answered.
 *
 * First member, in the teacher's own order, that is both reachable from here and actually
 * answered. `answered` is supplied by the caller because this package has no session: it hands
 * back a predicate rather than reaching for one, which also makes the ordering testable without
 * standing up a run.
 *
 * Returns null when nothing resolved — the caller then shows `fallback`, or nothing.
 */
export function resolveAlias(alias, referringTaskId, tasks, answered, 
/**
 * Where the chip SITS — see `ReachSite`, and it decides whether this resolves at all.
 *
 * A same-task member is `never` from a block and `always` from the exit, and this skips
 * `never` members. Without the site an exit's same-task reference was skipped like any dead
 * address and the chip fell back to printing its own name: "You picked Start of Split
 * pathway · MCQ 1" where the student's answer belonged.
 */
site) {
    for (const member of alias.members) {
        if (memberReach(member, referringTaskId, tasks, site) === 'never')
            continue;
        if (answered(member))
            return member;
    }
    return null;
}
/**
 * The alias an address belongs to, or undefined.
 *
 * An address belongs to AT MOST ONE alias — otherwise "renaming is universal" is false, because
 * the same answer would answer to two names. Nothing in the type enforces it, so
 * `aliasInvariants` below is what actually holds the line.
 */
export function aliasForRef(aliases, ref) {
    return aliases.find(a => a.members.some(m => sameRef(m, ref)));
}
export function sameRef(a, b) {
    return a.taskId === b.taskId && a.blockId === b.blockId && a.facet === b.facet;
}
/**
 * Find the alias for an address or describe the one to create — the insert path.
 *
 * Returning a description rather than minting an id keeps this pure: ids come from the caller,
 * which is also the layer that knows whether it is allowed to write.
 */
export function aliasToInsert(aliases, ref) {
    const existing = aliasForRef(aliases, ref);
    // A name of null means "derive from the single member", so a fresh alias starts unnamed and
    // keeps following the source block's name until a teacher decides otherwise.
    return existing ? { existing } : { create: { name: null, members: [ref] } };
}
/**
 * The two things that must be true of an alias set, as a list of what is wrong with this one.
 *
 * Checked rather than enforced by construction because aliases arrive from the wire, from a
 * paste and from a duplication remap, and a type cannot speak for any of those.
 */
export function aliasInvariants(aliases) {
    const problems = [];
    const seen = new Map();
    for (const alias of aliases) {
        // An alias with no members names nothing and would render as a permanent broken chip.
        if (alias.members.length === 0)
            problems.push(`alias ${alias.id} has no members`);
        for (const m of alias.members) {
            const key = `${m.taskId}|${m.blockId}|${m.facet}`;
            const owner = seen.get(key);
            if (owner && owner !== alias.id) {
                problems.push(`address ${key} is in both ${owner} and ${alias.id}`);
            }
            seen.set(key, alias.id);
        }
    }
    return problems;
}
const CHIP_TAG = () => /<span\b[^>]*\bdata-answer-ref-(?:alias|block)=[^>]*>/g;
/** The whole element, for a rewrite that has to reach the chip's text as well as its tag. */
const CHIP_ELEMENT = () => /<span\b[^>]*\bdata-answer-ref-(?:alias|block)=[^>]*>[^<]*<\/span>/g;
const attrOf = (tag, name) => new RegExp(`\\b${name}="([^"]*)"`).exec(tag)?.[1] ?? null;
/**
 * Every chip in the experience and which alias it names.
 *
 * The same walk the address census did, one level up: a chip now carries an alias id, so where
 * it SITS is still found by walking block data but what it POINTS AT is a lookup rather than a
 * parse. A chip with no alias id is reported with an empty one — that is a pre-alias chip or
 * one pasted without its alias, and the manager needs to list it in order to offer the repoint
 * that fixes it.
 *
 * Generic over the data shape for the reason the address census gives: prose lives at a dozen
 * addresses inside block data, and a table of every block's field names here would be a second
 * place to update whenever a block grows a rich-text field.
 */
/**
 * THE ALIASES NOTHING NAMES ANY MORE.
 *
 * An alias is minted when a chip is inserted and nothing has ever removed one. Deleting the
 * chip — or the block holding it, or retyping the sentence over it — leaves the alias behind,
 * owning its address and invisible: the manager lists references used in THIS task, so a name
 * no chip mentions appears on no screen at all. It is not inert, either. The next chip written
 * at that address finds it by address and silently inherits whatever the old one was called.
 *
 * WHY NOT AT THE MOMENT THE CHIP GOES. Deleting a chip and putting it straight back is one
 * thought, not two, and a sweep that fired on the keystroke would destroy the name in between
 * and leave the restored chip pointing at nothing. So the caller runs this at the boundary
 * where the teacher has said "done" — leaving the block — and a delete-and-restore inside one
 * visit never reaches it.
 *
 * Counted across EVERY task, never one: a chip in another task is still a chip, and sweeping
 * on the current task's census alone would delete a name that is in use one screen away.
 *
 * ## A DRIVEN-BY LINK IS A CITATION, and leaving it out was data loss
 *
 * `drivenBy: 'alias:<id>'` names an alias without placing a chip anywhere, so an alias whose
 * only citation is a driven axis read as unused and the sweep deleted it on leaving the block.
 * The link survived, pointing at nothing, and every authored version with it: measured in the
 * browser — select the driven block, click to another, and the alias is gone from the document
 * while `drivenBy` still names it. Exactly the failure the formula-token note below describes,
 * at a second site. The two stored alias drivers escaped only because chips happened to cite
 * their aliases too.
 *
 * NOT through `parseDriverAddress`, which is the canonical reader of this field and lives in
 * `driven-versions.ts` — that module imports this one, so using it here would close a cycle.
 * The prefix is a stored format and cannot change without a migration, which is what makes the
 * second spelling safe; a test pins them equal.
 */
export function unusedAliases(tasks, aliases) {
    if (aliases.length === 0)
        return [];
    const cited = new Set(collectAliasUsages(tasks).map(u => u.aliasId));
    for (const task of tasks) {
        for (const block of task.blocks ?? []) {
            const by = block.data?.drivenBy;
            if (typeof by === 'string' && by.startsWith('alias:'))
                cited.add(by.slice('alias:'.length));
        }
    }
    return aliases.filter(a => !cited.has(a.id));
}
/**
 * A RECALL AS A FORMULA STORES IT — `[ara_...]`, not a `<span>`.
 *
 * Every scan in this file was written when a recall could only live in rich text, so each one
 * opens with `value.includes('data-answer-ref-')` and returns early. A formula string carries
 * no such attribute: the chip is markup only while the field is mounted, and what is SAVED is
 * the bare token.
 *
 * The consequence was not a missing feature, it was data loss. `unusedAliases` saw no citation,
 * the orphan sweep deleted the alias on leaving the block, and the formula kept a token
 * pointing at nothing — which renders as literal `[ara_...]` text, for ever. Reported as "the
 * chip is gone and the text string is there instead".
 *
 * Narrow on purpose. Matching any `[...]` would claim mathjs matrix syntax and any prose
 * containing brackets; requiring the `ara_` prefix means a false positive needs someone to
 * type a real alias id by hand.
 */
const FORMULA_REF = () => /\[(ara_[0-9a-zA-Z-]+)\]/g;
export function collectAliasUsages(tasks) {
    const out = [];
    for (const task of tasks) {
        /*
         * EXIT BLOCKS COUNT, and leaving them out was data loss of exactly the kind the
         * `FORMULA_REF` note above describes.
         *
         * A task's exit text is authored in blocks like any other prose, but it is stored on the
         * FLOW — `flow.exits[slot].blocks` — not in `task.blocks`, so this walk never saw it. A
         * recall chip inserted into an exit therefore cited nothing: `unusedAliases` called its
         * alias an orphan, the sweep deleted it on leaving the block, and the chip rendered
         * "Missing block" in Preview for ever after. Reported as exactly that.
         *
         * Their `inBlockId` is the EXIT block's own id, which is real and is deliberately not a
         * member of `task.blocks`. Every consumer that wants a block filters by a canvas block's
         * id and so ignores these, which is right: an exit chip's problems belong to the exit, and
         * that surface has no issue bar to report them in.
         */
        const exitBlocks = Object.entries(task.flow?.exits ?? {})
            .flatMap(([slot, content]) => (content?.blocks ?? []).map(b => ({ block: b, slot })));
        const sites = [
            ...(task.blocks ?? []).map(block => ({ block })),
            ...exitBlocks,
        ];
        for (const { block, slot } of sites) {
            const walk = (value, path) => {
                if (typeof value === 'string') {
                    // A citation in a FORMULA counts the same as one in prose — see `FORMULA_REF`.
                    for (const m of value.matchAll(FORMULA_REF())) {
                        out.push({ aliasId: m[1], inTaskId: task.id, inBlockId: block.id, path, inFormula: true, inExitSlot: slot });
                    }
                    if (!value.includes('data-answer-ref-'))
                        return;
                    for (const [tag] of value.matchAll(CHIP_TAG())) {
                        out.push({
                            aliasId: attrOf(tag, 'data-answer-ref-alias') ?? '',
                            inTaskId: task.id,
                            inBlockId: block.id,
                            path,
                            inFormula: false,
                            inExitSlot: slot,
                        });
                    }
                    return;
                }
                if (Array.isArray(value)) {
                    value.forEach((v, i) => walk(v, [...path, i]));
                    return;
                }
                if (value && typeof value === 'object') {
                    for (const [k, v] of Object.entries(value))
                        walk(v, [...path, k]);
                }
            };
            walk(block.data ?? {}, []);
        }
    }
    return out;
}
/**
 * The aliases a LOOSE SET OF BLOCKS names, for the clipboard.
 *
 * A chip stores an alias id and nothing else, so blocks copied away from their aliases paste in
 * naming nothing — and a dangling alias id, unlike a dangling address, carries no clue about
 * what it meant. The same reason the task clipboard carries them; blocks were simply missed.
 *
 * It takes BLOCKS rather than tasks because that is what a block copy has: the source task is
 * not coming along, and on a cross-experience paste it does not exist at the far end at all. So
 * what travels is the alias with its members' `label` epitaphs, and a reference that cannot
 * resolve there arrives named and repointable instead of anonymous and dead.
 */
export function aliasesNamedBy(blocks, aliases) {
    // One synthetic task, so the chip walk stays in one place. `id` is unused: the result is
    // filtered on alias id, and where the chips SAT is not what a block paste carries.
    const named = new Set(collectAliasUsages([
        { id: '', blocks, flow: undefined },
    ]).map(u => u.aliasId));
    return aliases.filter(a => named.has(a.id));
}
/**
 * WHICH OTHER TASKS A COPIED SET DEPENDS ON, transitively.
 *
 * Copy/paste has twice now moved something without what it points at: scenario sets, and these
 * references. A chip carries an alias id, the alias names an ADDRESS in some task, and pasting
 * the chip into an experience that has no such task leaves a reference that can never resolve.
 * Carrying the alias (see `aliasesNamedBy`) makes that legible; only carrying the TASK makes it
 * work. This is the set a paste has to offer to bring.
 *
 * Transitive because a pulled task can cite a third one: stopping at one hop would hand back a
 * set that is still broken, just one level further in, which is the worst of both.
 *
 * A member pointing at a task NOT in `scope.tasks` is skipped rather than reported. It is
 * already dangling where it sits, so there is nothing to pull and nothing a paste could fix —
 * that one is the manager's Re-point to answer, not this.
 */
export function referencedTasksFor(scope, seedTaskIds) {
    // Indexed once. The walk revisits tasks, and re-running the chip census per hop is the kind of
    // quadratic that only shows up on the experience big enough to matter.
    const namedBy = new Map();
    for (const u of collectAliasUsages(scope.tasks)) {
        const set = namedBy.get(u.inTaskId);
        if (set)
            set.add(u.aliasId);
        else
            namedBy.set(u.inTaskId, new Set([u.aliasId]));
    }
    /*
     * THE SECOND KIND OF EDGE: a block whose VERSIONS are chosen by an answer in another task.
     *
     * This walk saw only chips, so a task holding a driven block offered to bring nothing and
     * every one of those blocks arrived with no driver — the same failure the walk was written
     * to stop, through the one door it did not watch. A driver names its task directly rather
     * than through an alias, so it is indexed here instead of being squeezed into `namedBy`.
     */
    const drivenBy = new Map();
    for (const task of scope.tasks) {
        for (const block of task.blocks ?? []) {
            const address = block.data?.drivenBy;
            if (typeof address !== 'string')
                continue;
            /* No guard on the half being present: an empty id matches no live task, and `reach`
               refuses anything not in this document for the same reason it refuses a deleted one. */
            const [targetTaskId = ''] = address.split(':');
            const set = drivenBy.get(task.id);
            if (set)
                set.add(targetTaskId);
            else
                drivenBy.set(task.id, new Set([targetTaskId]));
        }
    }
    const live = new Set(scope.tasks.map(t => t.id));
    const have = new Set(seedTaskIds);
    const found = new Map();
    let frontier = [...seedTaskIds];
    for (let depth = 0; frontier.length > 0; depth += 1) {
        const next = [];
        const reach = (from, taskId, drives) => {
            if (have.has(taskId) || !live.has(taskId))
                return;
            const already = found.get(taskId);
            if (already) {
                // Two tasks can cite one answer. One row, both reasons.
                if (!already.citedBy.includes(from))
                    already.citedBy.push(from);
                // And one row can be reached BOTH ways. The costlier reason is the one worth saying.
                already.drives ||= drives;
                return;
            }
            found.set(taskId, { taskId, citedBy: [from], depth, drives });
            next.push(taskId);
        };
        for (const from of frontier) {
            for (const aliasId of namedBy.get(from) ?? []) {
                // Also where a LEGACY chip stops: it names no alias, so `aliasId` is the empty string,
                // nothing is found, and there is no member to follow. No separate guard for it above —
                // one that can never change the answer is a line no test can hold down.
                const alias = scope.aliases.find(a => a.id === aliasId);
                if (!alias)
                    continue;
                for (const m of alias.members)
                    reach(from, m.taskId, false);
            }
            for (const taskId of drivenBy.get(from) ?? [])
                reach(from, taskId, true);
        }
        for (const id of next)
            have.add(id);
        frontier = next;
    }
    return [...found.values()];
}
/**
 * THE SAME ANSWER, FOUND LOCALLY — by its own id, or by what it was copied from.
 *
 * An address written against another document names ids that mean nothing here. But a task
 * pasted in records the task it came from, and so does every block in it, so a foreign address
 * can still be recognised: find the task that IS it or was COPIED from it, then the block
 * likewise. Both halves or nothing — a task without its block is half an address, which
 * resolves to nothing or, worse, gets completed later by a block answering another question.
 */
export function localAddressFor(tasks, ref) {
    const task = tasks.find(t => t.id === ref.taskId)
        ?? tasks.find(t => t.heritageId === ref.taskId);
    if (!task)
        return null;
    const block = (task.blocks ?? []).find(b => b.id === ref.blockId)
        ?? (task.blocks ?? []).find(b => b.heritageId === ref.blockId);
    if (!block)
        return null;
    return { taskId: task.id, blockId: block.id, facet: ref.facet };
}
/**
 * A DEAD HELD ALIAS HAS NO CLAIM OVER A LIVE OFFERED ONE.
 *
 * The client's half of the rule the paste endpoint applies. It is needed separately because
 * that endpoint only runs when there is a TASK to pull: paste the same block again once its
 * task is already here and nothing calls the server, so a ghost address would survive every
 * retry. Which is exactly what a teacher does after seeing a reference come in broken.
 *
 * Narrow in the same three ways: an alias with any member still resolving keeps everything,
 * an offered address that does not resolve here is refused rather than swapped in over an
 * epitaph, and only MEMBERS move — the name and fallback are the teacher's.
 */
export function planAliasRepairs(tasks, held, offered) {
    const out = [];
    for (const alias of held) {
        if (alias.members.length === 0)
            continue;
        if (alias.members.some(m => localAddressFor(tasks, m)?.taskId === m.taskId))
            continue;
        const from = offered.find(a => a.id === alias.id);
        if (!from)
            continue;
        const members = [];
        for (const m of from.members) {
            const local = localAddressFor(tasks, m);
            // The epitaph travels with the address it describes, so a later break still says what
            // this meant rather than going anonymous the moment it is repaired once.
            if (local)
                members.push(m.label === undefined ? local : { ...local, label: m.label });
        }
        if (members.length === 0)
            continue;
        out.push({ aliasId: alias.id, members });
    }
    return out;
}
/**
 * Which aliases would stop publication, and which chips name nothing at all.
 *
 * Two separate failures with one answer. A chip whose alias is gone can never resolve; an alias
 * every one of whose members is unreachable from where it is used can never resolve either.
 * Both are red rows in the manager and both block publication, which is why they are counted
 * together rather than reported as two kinds of nothing.
 */
export function brokenAliasUsages(scope) {
    return collectAliasUsages(scope.tasks).filter(u => {
        const alias = scope.aliases.find(a => a.id === u.aliasId);
        return !alias || aliasStatus(alias, u.inTaskId, scope.tasks) === 'broken';
    });
}
/**
 * The members an alias would have without `member`, or null when that is not allowed.
 *
 * Null for the last one. An alias with no members names nothing and renders as a permanent
 * broken chip, so removing the final member is not a smaller version of removing one — it is
 * deleting the reference, which is done by deleting the chip. Returning null rather than an
 * empty array makes the caller say which it meant.
 *
 * Here rather than in the panel because it is the same rule `aliasInvariants` enforces, and a
 * rule stated in two places is a rule that will be stated differently in two places.
 */
export function membersWithout(alias, member) {
    if (alias.members.length <= 1)
        return null;
    const next = alias.members.filter(m => !sameRef(m, member));
    // Nothing matched: the caller named something this alias does not hold, which is a bug
    // rather than a no-op, and silently succeeding would hide it.
    return next.length === alias.members.length ? null : next;
}
/**
 * Point every chip naming `fromAliasId` at `toAliasId` instead.
 *
 * The repair path for a reference that cannot be fixed by editing its alias, of which there
 * are two kinds and both were dead ends until now: a chip whose alias is GONE (nothing to
 * edit), and a chip authored before aliases existed (no alias id at all, matched by passing
 * `''`). Re-point appeared to work on both and silently changed nothing.
 *
 * Returns the identical object when nothing matched, so a caller can use it as the "did
 * anything change" test, exactly as `repointAnswerRefs` does.
 */
export function retargetChips(data, fromAliasId, toAliasId, fallback) {
    let touched = false;
    const rewrite = (html) => html.replace(CHIP_ELEMENT(), element => {
        const tag = CHIP_TAG().exec(element)?.[0] ?? '';
        if ((attrOf(tag, 'data-answer-ref-alias') ?? '') !== fromAliasId)
            return element;
        touched = true;
        const withAlias = tag.includes('data-answer-ref-alias=')
            ? tag.replace(/\bdata-answer-ref-alias="[^"]*"/, `data-answer-ref-alias="${toAliasId}"`)
            // A legacy chip has no alias attribute to replace, so one is added — and its old
            // ADDRESS attributes are dropped with it, since nothing reads them any more and
            // leaving them would make the chip look like it still names two things.
            : tag.replace(/<span\b/, `<span data-answer-ref-alias="${toAliasId}"`);
        const cleaned = withAlias
            .replace(/\s*\bdata-answer-ref-task="[^"]*"/, '')
            .replace(/\s*\bdata-answer-ref-block="[^"]*"/, '')
            .replace(/\s*\bdata-answer-ref-facet="[^"]*"/, '')
            .replace(/\s*\bdata-answer-ref-fallback="[^"]*"/, '')
            .replace(/>$/, ` data-answer-ref-fallback="${fallback}">`);
        return `${cleaned}${fallback}</span>`;
    });
    /* A formula token is re-pointed by swapping the id inside the brackets — there is no markup
       to rebuild, and the chip's label is derived from the alias at render time anyway. */
    const rewriteFormula = (text) => text.replace(FORMULA_REF(), (whole, id) => {
        if (id !== fromAliasId)
            return whole;
        touched = true;
        return `[${toAliasId}]`;
    });
    const walk = (value) => {
        if (typeof value === 'string') {
            const withFormula = rewriteFormula(value);
            return withFormula.includes('data-answer-ref-') ? rewrite(withFormula) : withFormula;
        }
        if (Array.isArray(value))
            return value.map(walk);
        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, walk(v)]));
        }
        return value;
    };
    const next = walk(data);
    return touched ? next : data;
}
/**
 * THE NAME A WHOLE BLOCK GOES BY — one name, worn by its parts.
 *
 * A Numerical answers with a value, an uncertainty, a unit and a whole, and each of those is
 * its own alias with its own name. Rename one and the others went on deriving from the
 * BLOCK, so `kayak-vel` sat beside `Landing Location` and nothing said they were the same
 * question. Reported as "i changed the name of full number numerical to kayak-vel and then
 * got confused later as to what Landing Location was, not realizing that it was pretty much
 * the same recall except just the number".
 *
 * So a name given to ANY facet names the family, and the rest read `kayak-vel (number)`,
 * `kayak-vel (uncertainty)`, `kayak-vel (unit)`.
 *
 * ## AT MOST ONE NAME PER FAMILY, which is what makes this unambiguous
 *
 * The owner's rule: "renamed sibling overrides the family and becomes the family name ...
 * this is the easiest, as it keeps the family coordinated everywhere all the time". So a
 * rename CLEARS its siblings' names — see `familySiblingsToClear` — and the question "which
 * of several names wins" never arises. Most recent wins because it is the only one left.
 *
 * Searched by BLOCK, not by alias: two aliases are family when they quote the same block in
 * the same task, whatever facet each names.
 */
export function familyNameOf(ref, aliases) {
    for (const a of aliases) {
        if (!a.name)
            continue;
        if (a.members.some(m => m.taskId === ref.taskId && m.blockId === ref.blockId))
            return a.name;
    }
    return null;
}
/**
 * The other aliases a rename of `id` speaks for: everything quoting a block it quotes.
 *
 * Named or not. The name is a property of the FAMILY, so a rename writes it to all of them
 * and the question "which of several wins" never arises — there is one. A multi-member alias
 * is several blocks, so renaming it speaks for every family it belongs to.
 */
export function familySiblings(id, aliases) {
    const self = aliases.find(a => a.id === id);
    if (!self)
        return [];
    const blocks = new Set(self.members.map(m => `${m.taskId}\u0000${m.blockId}`));
    return aliases
        .filter(a => a.id !== id
        && a.members.some(m => blocks.has(`${m.taskId}\u0000${m.blockId}`)))
        .map(a => a.id);
}
/**
 * ONE EDIT TO A NAME, WRITTEN TO THE WHOLE FAMILY.
 *
 * A name belongs to the BLOCK, not to one facet of it: the owner's rule is "you can only
 * change the alias for the family ... (full), (number), (uncertainty), and so on are
 * automatically appended to the chip, but never on the alias". So a rename writes the same
 * name to every alias quoting the same block, and clearing one clears them all.
 *
 * The first version spread the name by CLEARING the siblings and letting them derive it back,
 * which read correctly and stored something else: `kv` beside three blanks, and a sibling
 * opened on its own showed an empty field with no indication of what it was. Reported as "i
 * renamed it to kv, kv (the number version) kept its name without any indicator what it is".
 *
 * Only a name does this. Re-pointing a member is not a statement about what the answer is
 * called, so a sibling's name survives it.
 *
 * Pure, and returns the whole list, because the page stores an alias edit as a snapshot op.
 */
export function applyAliasPatch(aliases, id, patch) {
    if (!('name' in patch))
        return aliases.map(a => (a.id === id ? { ...a, ...patch } : a));
    const name = typeof patch.name === 'string' && patch.name.trim() !== '' ? patch.name : null;
    const family = new Set([id, ...familySiblings(id, aliases)]);
    return aliases.map(a => (a.id === id
        ? { ...a, ...patch, name }
        : family.has(a.id) ? { ...a, name } : a));
}
//# sourceMappingURL=answer-ref-alias.js.map